import Spinner from "../../../../../shared/components/Spinner";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import { formatDateTimeLabel } from "../../../../../shared/utils/dateTime";
import type {
  Achievement,
  AchievementType,
} from "../../../shared/services/achievementsService";
import ProfileAccordionSection from "../ProfileAccordionSection";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

type ProfileAchievementsTabProps = {
  open: boolean;
  onToggle: () => void;
  achievements: Achievement[];
  loading: boolean;
  error: string;
};

function achievementTypeLabel(type: AchievementType) {
  const labels = siteTexts.profile.user.achievements.types;
  if (type === "organizer") return labels.organizer;
  if (type === "event") return labels.event;
  if (type === "ranking") return labels.ranking;
  if (type === "participation") return labels.participation;
  return labels.athlete;
}

export default function ProfileAchievementsTab({
  open,
  onToggle,
  achievements,
  loading,
  error,
}: ProfileAchievementsTabProps) {
  return (
    <ProfileAccordionSection
      id="profile-achievements"
      icon="workspace_premium"
      title={siteTexts.profile.user.achievements.title}
      subtitle={siteTexts.profile.user.achievements.description}
      open={open}
      onToggle={onToggle}
    >
      <section className="user-profile-achievements">
        <UserProfileSectionHeader
          className="user-profile-achievements-header"
          icon="workspace_premium"
          title={siteTexts.profile.user.achievements.title}
          description={siteTexts.profile.user.achievements.description}
          actions={
            <span className="user-profile-achievements-count">
              {siteTexts.profile.user.achievements.unlockedCountPrefix}{" "}
              {achievements.filter((item) => item.status === "unlocked").length}
              /{achievements.length}
            </span>
          }
        />

        {loading ? (
          <div className="user-profile-achievements-loading">
            <Spinner />
            <span>{siteTexts.profile.user.achievements.loading}</span>
          </div>
        ) : error ? (
          <p className="status-message">
            {siteTexts.profile.user.achievements.loadError}
          </p>
        ) : (
          <div className="user-profile-achievements-grid">
            {achievements.map((achievement) => {
              const unlocked = achievement.status === "unlocked";
              return (
                <article
                  key={achievement.id}
                  className={`user-profile-achievement-card ${
                    unlocked ? "is-unlocked" : "is-locked"
                  } ${achievement.rarity === "rare" ? "is-rare" : ""}`}
                >
                  <div className="user-profile-achievement-icon">
                    <span
                      className="material-symbols-outlined"
                      aria-hidden="true"
                    >
                      {achievement.icon}
                    </span>
                  </div>
                  <div className="user-profile-achievement-copy">
                    <div className="user-profile-achievement-title-row">
                      <h3>{achievement.name}</h3>
                      <span>{achievementTypeLabel(achievement.type)}</span>
                    </div>
                    <p>{achievement.description}</p>
                    <small>
                      {unlocked && achievement.unlockedAt
                        ? `${siteTexts.profile.user.achievements.unlockedAt} ${formatDateTimeLabel(
                            achievement.unlockedAt,
                          )}`
                        : siteTexts.profile.user.achievements.locked}
                    </small>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </ProfileAccordionSection>
  );
}
